import { useState, Fragment } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { FaExclamationTriangle } from "react-icons/fa";
import { toast, Slide } from "react-toastify";
import axios from "axios";
import { API_URL } from "../../../utilities/url";
import storage from "../../../services/storage";

// Number of selected tools above which we warn the user. LLM tool-selection
// accuracy degrades as the active tool set grows, so keep each agent focused.
const TOOL_WARN_THRESHOLD = 10;

// Two modes:
//  - import mode  (idtOsa provided): persists tools server-side immediately, then
//    onImported(draftPrompt) lets the parent refresh.
//  - stage mode   (onStage provided, used on the create-OSA page where no idt_osa
//    exists yet): hands the selected tools back as function-list rows + the draft,
//    to be saved when the new bot is created.
export default function ImportTools({
  show,
  handleClose,
  idtOsa,
  onImported,
  onStage,
}) {
  const [url, setUrl] = useState("");
  const [format, setFormat] = useState(null);
  const [tools, setTools] = useState([]);
  const [selected, setSelected] = useState({}); // name -> bool
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);

  // Auth declared by the spec; the secret is entered here, never read from spec.
  const [authScheme, setAuthScheme] = useState("bearer");
  const [authHeader, setAuthHeader] = useState("Authorization");
  const [authToken, setAuthToken] = useState("");

  const [draftPrompt, setDraftPrompt] = useState("");

  // Spec-level guidance + non-fatal import warnings, and which rows are expanded to
  // show their parameter detail (so precision is reviewable before importing).
  const [specInfo, setSpecInfo] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [expanded, setExpanded] = useState({}); // name -> bool

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${storage.get("access_token")}` },
  });

  const reset = () => {
    setUrl("");
    setFormat(null);
    setTools([]);
    setSelected({});
    setAuthToken("");
    setAuthScheme("bearer");
    setAuthHeader("Authorization");
    setDraftPrompt("");
    setSpecInfo(null);
    setWarnings([]);
    setExpanded({});
  };

  const close = () => {
    reset();
    handleClose();
  };

  const selectedNames = () =>
    tools.filter((t) => selected[t.name]).map((t) => t.name);

  const buildDraftPrompt = (fmt, picked, info) => {
    if (picked.length === 0) return "";
    const lines = picked.map((n) => `- ${n}`).join("\n");
    // Cross-cutting guidance from the spec's info.description (e.g. id conventions,
    // idempotency) gives the model context the per-tool descriptions don't repeat.
    const guidance =
      info && info.description
        ? `# Provider guidance\n${info.description}\n\n`
        : "";
    return (
      `These tools were imported from an external ${fmt || "spec"}.\n\n` +
      guidance +
      `Available tools:\n${lines}\n\n` +
      `# Orchestration (edit this for your process flow)\n` +
      `Describe when/how to use the tools, e.g. the required order ` +
      `("first call X to get an id, then call Y"), and any conditional logic. ` +
      `Tools that depend on data returned by another tool should be called in that order.`
    );
  };

  const handlePreview = async () => {
    if (!url) return;
    setLoadingPreview(true);
    try {
      const res = await axios.post(
        `${API_URL}preview-tools-from-spec`,
        { url },
        authHeaders()
      );
      if (res.data.success === true) {
        const data = res.data.data;
        setFormat(data.format);
        setTools(data.tools || []);
        setSpecInfo(data.info || null);
        setWarnings(data.warnings || []);
        setExpanded({});
        // Default selection: bindable tools only, capped at the warn threshold.
        const initial = {};
        (data.tools || []).forEach((t, i) => {
          initial[t.name] = !t.needsBinding && i < TOOL_WARN_THRESHOLD;
        });
        setSelected(initial);
        // Detect auth scheme/header from the first tool that declares it.
        const withAuth = (data.tools || []).find((t) => t.requiredAuth);
        if (withAuth) {
          setAuthScheme(withAuth.requiredAuth.scheme || "bearer");
          setAuthHeader(withAuth.requiredAuth.headerName || "Authorization");
        }
        setDraftPrompt(
          buildDraftPrompt(
            data.format,
            (data.tools || [])
              .filter((t, i) => initial[t.name])
              .map((t) => t.name),
            data.info
          )
        );
      } else {
        toast.error(res.data.message || "Failed to read spec", {
          position: "top-right",
          theme: "colored",
          transition: Slide,
          autoClose: 4000,
          hideProgressBar: true,
        });
      }
    } catch (e) {
      toast.error("Failed to read spec", {
        position: "top-right",
        theme: "colored",
        transition: Slide,
        autoClose: 4000,
        hideProgressBar: true,
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const toggle = (name) => {
    setSelected((prev) => {
      const next = { ...prev, [name]: !prev[name] };
      setDraftPrompt(
        buildDraftPrompt(
          format,
          tools.filter((t) => next[t.name]).map((t) => t.name),
          specInfo
        )
      );
      return next;
    });
  };

  const toggleExpand = (name) =>
    setExpanded((prev) => ({ ...prev, [name]: !prev[name] }));

  const setAll = (val) => {
    const next = {};
    tools.forEach((t) => {
      next[t.name] = val && !t.needsBinding;
    });
    setSelected(next);
    setDraftPrompt(
      buildDraftPrompt(
        format,
        tools.filter((t) => next[t.name]).map((t) => t.name),
        specInfo
      )
    );
  };

  // Apply the panel's auth secret + the tool's own header templates (in:header /
  // in:cookie params, a declared idempotency header) for stage mode, mirroring the
  // server-side buildToolRow. The secret is never read from the spec. apiKey-in-query
  // appends to the endpoint instead of a header.
  const applyAuth = (t) => {
    let endpoint = t.endpoint;
    const headers = { ...(t.headerTemplates || {}) };
    if (authToken) {
      const ra = t.requiredAuth || {};
      const scheme = authScheme || ra.scheme || "bearer";
      if (scheme === "apikey_query") {
        const qn = ra.queryName || "api_key";
        const sep = endpoint.includes("?") ? "&" : "?";
        endpoint += `${sep}${encodeURIComponent(qn)}=${encodeURIComponent(
          authToken
        )}`;
      } else if (scheme === "bearer") {
        headers[authHeader] = `Bearer ${authToken}`;
      } else {
        headers[authHeader] = authToken;
      }
    }
    return {
      endpoint,
      headers: Object.keys(headers).length ? JSON.stringify(headers) : null,
    };
  };

  // Convert a flat JSON-Schema object into the function-list param-array shape
  // the create/edit forms use.
  const toParamArray = (paramsObj) => {
    const props = (paramsObj && paramsObj.properties) || {};
    const required = (paramsObj && paramsObj.required) || [];
    return Object.entries(props).map(([key, value]) => ({
      name: key,
      type: (value && value.type) || "string",
      description: (value && value.description) || "",
      enum: (value && value.enum) || [],
      required: required.includes(key),
    }));
  };

  const stageSelected = () => {
    const items = tools
      .filter((t) => selected[t.name] && !t.needsBinding)
      .map((t) => {
        const a = applyAuth(t);
        return {
          name: t.name,
          description: t.description,
          parameters: toParamArray(t.parameters),
          active: "Y",
          exec_type: "api",
          endpoint: a.endpoint,
          method: t.method,
          headers: a.headers,
          body_type: t.bodyType || "",
          body: t.body || "",
          credentials: null,
          response_filter: null,
          response_prompt: null,
          response_media_type: "text",
          response_media_url: null,
        };
      });
    if (onStage) onStage(items, draftPrompt);
    toast.success(`${items.length} tool(s) added`, {
      position: "top-right",
      theme: "colored",
      transition: Slide,
      autoClose: 3000,
      hideProgressBar: true,
    });
    close();
  };

  const handleImport = async () => {
    const picked = selectedNames();
    if (picked.length === 0) {
      toast.error("Select at least one tool to import", {
        position: "top-right",
        theme: "colored",
        transition: Slide,
        autoClose: 3000,
        hideProgressBar: true,
      });
      return;
    }

    // Create-page (no idt_osa yet): stage into the form instead of persisting.
    if (!idtOsa && onStage) {
      stageSelected();
      return;
    }

    setImporting(true);
    try {
      const res = await axios.post(
        `${API_URL}import-tools-from-spec`,
        {
          idt_osa: idtOsa,
          url,
          selected: picked,
          auth: {
            scheme: authScheme,
            headerName: authHeader,
            token: authToken,
          },
        },
        authHeaders()
      );
      if (res.data.success === true) {
        toast.success(res.data.message || "Tools imported", {
          position: "top-right",
          theme: "colored",
          transition: Slide,
          autoClose: 4000,
          hideProgressBar: true,
        });
        if (onImported) onImported(draftPrompt);
        close();
      } else {
        toast.error(res.data.message || "Failed to import tools", {
          position: "top-right",
          theme: "colored",
          transition: Slide,
          autoClose: 4000,
          hideProgressBar: true,
        });
      }
    } catch (e) {
      toast.error("Failed to import tools", {
        position: "top-right",
        theme: "colored",
        transition: Slide,
        autoClose: 4000,
        hideProgressBar: true,
      });
    } finally {
      setImporting(false);
    }
  };

  const selectedCount = selectedNames().length;

  return (
    <Modal show={show} onHide={close} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Import Tools from URL</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Spec URL */}
        <div className="mb-3">
          <label className="form-label">
            Spec URL (OpenAPI 3.x or tools.json)
          </label>
          <div className="d-flex">
            <input
              type="url"
              className="form-control me-2"
              placeholder="https://api.example.com/openapi.json"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <Button
              variant="primary"
              onClick={handlePreview}
              disabled={!url || loadingPreview}
            >
              {loadingPreview ? "Fetching..." : "Fetch"}
            </Button>
          </div>
        </div>

        {tools.length > 0 && (
          <>
            {/* Auth */}
            <div className="mb-3 border rounded p-3">
              <div className="fw-bold mb-2">Authentication</div>
              <div className="row g-2">
                <div className="col-sm-3">
                  <label className="form-label">Scheme</label>
                  <select
                    className="form-select"
                    value={authScheme}
                    onChange={(e) => setAuthScheme(e.target.value)}
                  >
                    <option value="bearer">Bearer</option>
                    <option value="apikey">API Key (header)</option>
                  </select>
                </div>
                <div className="col-sm-4">
                  <label className="form-label">Header name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={authHeader}
                    onChange={(e) => setAuthHeader(e.target.value)}
                  />
                </div>
                <div className="col-sm-5">
                  <label className="form-label">Token / API key</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Paste the secret"
                    value={authToken}
                    onChange={(e) => setAuthToken(e.target.value)}
                  />
                </div>
              </div>
              <small className="text-muted">
                The secret is stored with the OSA and sent on each tool call. It
                is never read from the spec.
              </small>
            </div>

            {/* Tool checklist */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-bold">
                Tools{" "}
                <span className="text-muted">
                  ({format} — {selectedCount} selected)
                </span>
              </div>
              <div>
                <a className="me-2" role="button" onClick={() => setAll(true)}>
                  Select all
                </a>
                <a role="button" onClick={() => setAll(false)}>
                  Clear
                </a>
              </div>
            </div>

            {selectedCount > TOOL_WARN_THRESHOLD && (
              <div className="alert alert-warning py-2">
                <FaExclamationTriangle className="me-1" />
                You selected {selectedCount} tools. Large tool sets reduce the
                model's tool-selection accuracy — consider keeping each agent
                focused on the tools it actually needs.
              </div>
            )}

            {warnings.length > 0 && (
              <div className="alert alert-warning py-2 small">
                <FaExclamationTriangle className="me-1" />
                Some parts of the spec were skipped:
                <ul className="mb-0">
                  {warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="table-responsive" style={{ maxHeight: 280 }}>
              <table className="table table-bordered table-sm">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th>Name</th>
                    <th>Method</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {tools.map((t) => (
                    <Fragment key={t.name}>
                      <tr>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!!selected[t.name]}
                            disabled={t.needsBinding}
                            onChange={() => toggle(t.name)}
                          />
                        </td>
                        <td>
                          {t.name}
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2 align-baseline"
                            onClick={() => toggleExpand(t.name)}
                          >
                            {expanded[t.name] ? "hide" : "params"}
                          </button>
                          {t.needsBinding && (
                            <div className="text-danger small">
                              <FaExclamationTriangle className="me-1" />
                              No endpoint in spec — configure manually
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-secondary">{t.method}</span>
                        </td>
                        <td className="small">{t.description}</td>
                      </tr>
                      {expanded[t.name] && (
                        <tr className="bg-light">
                          <td></td>
                          <td colSpan={3}>
                            {toParamArray(t.parameters).length === 0 ? (
                              <div className="text-muted small">
                                No parameters.
                              </div>
                            ) : (
                              <table className="table table-sm mb-1">
                                <thead>
                                  <tr>
                                    <th>Param</th>
                                    <th>Type</th>
                                    <th>Req</th>
                                    <th>Enum</th>
                                    <th>Description</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {toParamArray(t.parameters).map((p) => (
                                    <tr key={p.name}>
                                      <td>
                                        <code>{p.name}</code>
                                      </td>
                                      <td className="small">{p.type}</td>
                                      <td className="text-center">
                                        {p.required ? "✓" : ""}
                                      </td>
                                      <td className="small">
                                        {(p.enum || []).join(", ")}
                                      </td>
                                      <td className="small">{p.description}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            <div className="small text-muted">
                              {t.method} {t.endpoint}
                              {t.bodyType ? ` · body: ${t.bodyType}` : ""}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Suggested, editable prompt draft */}
            <div className="mt-3">
              <label className="form-label fw-bold">
                Suggested instructions (draft — review before applying)
              </label>
              <textarea
                className="form-control"
                rows={6}
                value={draftPrompt}
                onChange={(e) => setDraftPrompt(e.target.value)}
              />
              <small className="text-muted">
                On import this draft is appended to the OSA's instructions field
                for you to edit; it is never auto-applied to a running agent.
              </small>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={close}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleImport}
          disabled={importing || selectedCount === 0}
        >
          {importing
            ? "Importing..."
            : `${idtOsa ? "Import" : "Add"} ${selectedCount} tool(s)`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
